const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";

export type Role = "SYSTEM_MASTER" | "ORG_ADMIN" | "EDITOR" | "VIEWER";

export interface SessionUser {
  userId: string;
  organizationId: string;
  email: string;
  role: Role;
  allowedClientIds: string[];
}

export type JobStatus = "UPLOADED" | "ANALYZING" | "PROCESSING" | "REVIEW_REQUIRED" | "COMPLETED" | "FAILED";

export interface ProcessingJob {
  jobId: string;
  organizationId: string;
  clientId: string;
  fileName: string;
  objectKey: string;
  status: JobStatus;
  uploadMode: string;
  message?: string;
  outputKey?: string;
  counts?: {
    journalEntries: number;
    unprocessedItems: number;
    proposedAliases: number;
    proposedRules: number;
  };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type SheetRole = "target" | "history" | "rules" | "aliases" | "unknown";

export interface WorkbookSheetStructure {
  name: string;
  role: SheetRole;
  headers: string[];
  sampleRows: Record<string, unknown>[];
  headerRowNumber?: number;
  dataStartRowNumber?: number;
  nonEmptyRowCount?: number;
}

export interface WorkbookStructureReview {
  fileName: string;
  analysis: {
    targetSheetName: string;
    historySheetName?: string;
    ruleSheetName?: string;
    aliasSheetName?: string;
    columnMappings: Record<string, string>;
    confidence: number;
    notes: string[];
  };
  sheets: WorkbookSheetStructure[];
}

export async function apiGet<T>(path: string, token: string): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (!response.ok) throw new Error(await errorMessage(response, `GET ${path} failed: ${response.status}`));
  return response.json() as Promise<T>;
}

export async function apiPost<T>(path: string, token: string, body: unknown): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) throw new Error(await errorMessage(response, `POST ${path} failed: ${response.status}`));
  return response.json() as Promise<T>;
}

export async function createUpload(clientId: string, file: File, token: string) {
  const contentBase64 = await fileToBase64(file);
  const response = await fetch(`${apiBaseUrl}/uploads`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json"
    },
    body: JSON.stringify({ clientId, fileName: file.name, contentBase64 })
  });
  if (!response.ok) throw new Error(await errorMessage(response, `Upload request failed: ${response.status}`));
  return response.json() as Promise<ProcessingJob>;
}

export function templateUrl(clientId: string) {
  return `${apiBaseUrl}/clients/${encodeURIComponent(clientId)}/template`;
}

export async function analyzeWorkbook(clientId: string, file: File, token: string): Promise<WorkbookStructureReview> {
  const contentBase64 = await fileToBase64(file);
  return apiPost<WorkbookStructureReview>("/workbooks/analyze", token, {
    clientId,
    fileName: file.name,
    contentBase64
  });
}

export async function downloadTemplate(clientId: string, token: string): Promise<void> {
  const response = await fetch(templateUrl(clientId), {
    headers: { authorization: `Bearer ${token}` }
  });
  if (!response.ok) throw new Error(await errorMessage(response, `Template download failed: ${response.status}`));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `zeimu-template-${clientId}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadJobResult(jobId: string, token: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/jobs/${encodeURIComponent(jobId)}/result`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (!response.ok) throw new Error(await errorMessage(response, `Result download failed: ${response.status}`));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `shiwake-result-${jobId}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

async function errorMessage(response: Response, fallback: string) {
  try {
    const payload = await response.json() as { message?: string };
    return payload.message || fallback;
  } catch {
    return fallback;
  }
}

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
